/**
 * OAuth bounded-context widget registry.
 *
 * Each widget is registered via the typed factory (`defineStatWidget` /
 * `defineChartWidget` / `defineTableWidget`) and exposed as `provideWidget(...)`
 * entries. Spread `oAuthWidgetProviders` in the oAuth providers array — same
 * pattern as `iamWidgetProviders` in `iam/widgets/iam.widgets.ts`.
 *
 * Widgets declared here:
 *   - `oAuth.activeTokens.stat`          — stat-tile: total issued access tokens (RefreshableWidget)
 *   - `oAuth.clientsByGrantType.chart`   — bar chart: OAuth client distribution by grant type
 *   - `oAuth.recentAccessTokens.table`   — table: latest 10 access tokens with status and expiry
 *
 * Icons are SVGs served from a CDN (Iconify) via `iconUrl`, NOT `@ng-icons` imports —
 * the catalog renders them with an `<img>`, so a BC adds widgets without registering
 * icon symbols. A `?color=` keeps each glyph visible on both light and dark surfaces.
 *
 * Permission gates:
 *   - `oAuth.accessToken.get` — the exact @Auth string on OAuthPaginateAccessTokensResolver
 *     and OAuthGetAccessTokensResolver (used by widgets 1 and 3).
 *   - `oAuth.client.get`      — the exact @Auth string on OAuthPaginateClientsResolver
 *     and OAuthGetClientsResolver (used by widget 2).
 * This ensures that widget visibility always coincides with data-access permission,
 * so an account can never see a widget whose underlying query would return 403.
 */
import type { ChartSeries, TableColumnDef } from '@aurora/modules/widget';
import {
  defineChartWidget,
  defineStatWidget,
  defineTableWidget,
  provideWidget,
  WIDGET_CATEGORY,
} from '@aurora/modules/widget';
import gql from 'graphql-tag';

// ── Stat data source ──────────────────────────────────────────────────────────

/**
 * Dedicated count query — uses the explicit `oAuthCountAccessToken` resolver (Int scalar).
 * Replaces the old `paginate(limit:0)` stopgap; no rows are fetched, no variables needed.
 */
const accessTokenCountQuery = gql`
  query OAuthCountAccessTokens($query: QueryStatementInput, $constraint: QueryStatementInput) {
    count: oAuthCountAccessToken(query: $query, constraint: $constraint)
  }
`;

/**
 * Maps the raw oAuthCountAccessToken response to a plain number.
 * The resolver returns a nullable scalar Int aliased as `count`.
 */
function mapCountToTotal(raw: unknown): number {
  return (raw as { count?: number | null }).count ?? 0;
}

// ── Chart data source ─────────────────────────────────────────────────────────

/**
 * Lightweight query: fetch only `id` and `grantType` for all clients so we can
 * group by grant type client-side without a server-side group-by endpoint.
 * Uses `limit: 10_000` — clients is a bounded-size admin entity.
 */
const clientGrantTypesQuery = gql`
  query OAuthGetClientGrantTypes($query: QueryStatementInput, $constraint: QueryStatementInput) {
    objects: oAuthGetClients(query: $query, constraint: $constraint) {
      id
      grantType
    }
  }
`;

interface ClientGrantTypeItem {
  id: string;
  grantType: string;
}

/**
 * Maps the raw oAuthGetClients response to a `ChartSeries[]` bar series.
 *
 * Counts occurrences of each `grantType` value and produces one named data-point
 * per grant type. The stable `localeCompare` sort ensures each type always occupies
 * the same palette slot across refreshes (ECharts assigns colors by position).
 */
function mapClientsToBarSeries(raw: unknown): ChartSeries[] {
  const data = raw as { objects?: ClientGrantTypeItem[] };
  const clients = data?.objects ?? [];

  const counts = clients.reduce<Record<string, number>>((acc, client) => {
    const key = client.grantType ?? 'UNKNOWN';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  const sortedData = Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => ({ name, value }));

  return [{ data: sortedData }];
}

// ── Table data source ─────────────────────────────────────────────────────────

/**
 * Targeted query: fetch only the fields needed by the recent-tokens table.
 * Ordered by `createdAt_DESC` so the latest tokens appear first.
 */
const accessTokenRecentQuery = gql`
  query OAuthGetRecentAccessTokens($query: QueryStatementInput, $constraint: QueryStatementInput) {
    objects: oAuthGetAccessTokens(query: $query, constraint: $constraint) {
      id
      rowId
      isRevoked
      expiresAt
      createdAt
    }
  }
`;

interface AccessTokenRow {
  id: string;
  isRevoked: boolean;
  expiresAt: string;
  createdAt: string;
}

/**
 * Maps the raw oAuthGetAccessTokens response to a flat row array for the table
 * archetype. Each row is cast to `Record<string, unknown>` as required by
 * `defineTableWidget`.
 */
function mapAccessTokensToRows(raw: unknown): Record<string, unknown>[] {
  const data = raw as { objects?: AccessTokenRow[] };
  return (data?.objects ?? []) as unknown as Record<string, unknown>[];
}

// ── Column definitions ────────────────────────────────────────────────────────

const accessTokenColumns: TableColumnDef[] = [
  { key: 'rowId', header: 'o-auth.access-token.RowId' },
  { key: 'isRevoked', header: 'o-auth.access-token.IsRevoked' },
  { key: 'expiresAt', header: 'o-auth.access-token.ExpiresAt' },
];

// ── Widget definitions ────────────────────────────────────────────────────────

export const oAuthWidgetProviders = [
  provideWidget(
    defineStatWidget({
      id: 'oAuth.activeTokens.stat',
      translation: 'dashboard.widgets.oAuth.IssuedTokensStat',
      description: 'dashboard.widgets.oAuth.IssuedTokensStatDescription',
      iconUrl: 'https://api.iconify.design/lucide/key-round.svg?color=%2310b981',
      permission: 'oAuth.accessToken.get',
      category: WIDGET_CATEGORY.KPIS,
      span: {
        minCols: 2,
        maxCols: 6,
        minRows: 1,
        maxRows: 2,
        defaultCols: 3,
        defaultRows: 1,
      },
      format: 'compact',
      source: {
        kind: 'graphql',
        document: accessTokenCountQuery,
        map: mapCountToTotal,
        fetchPolicy: 'network-only',
      },
    }),
  ),
  provideWidget(
    defineChartWidget({
      id: 'oAuth.clientsByGrantType.chart',
      translation: 'dashboard.widgets.oAuth.ClientsByGrantTypeChart',
      description: 'dashboard.widgets.oAuth.ClientsByGrantTypeChartDescription',
      iconUrl: 'https://api.iconify.design/lucide/chart-bar.svg?color=%23f59e0b',
      permission: 'oAuth.client.get',
      category: WIDGET_CATEGORY.CHARTS,
      span: {
        minCols: 4,
        maxCols: 12,
        minRows: 4,
        maxRows: 8,
        defaultCols: 6,
        defaultRows: 5,
      },
      chartType: 'bar',
      i18nLabels: true,
      i18nLabelKeyPrefix: 'o-auth.client.GrantType.',
      source: {
        kind: 'graphql',
        document: clientGrantTypesQuery,
        variables: { query: { offset: 0, limit: 10_000 } },
        map: mapClientsToBarSeries,
        fetchPolicy: 'network-only',
      },
    }),
  ),
  provideWidget(
    defineTableWidget({
      id: 'oAuth.recentAccessTokens.table',
      translation: 'dashboard.widgets.oAuth.RecentAccessTokensTable',
      description: 'dashboard.widgets.oAuth.RecentAccessTokensTableDescription',
      iconUrl: 'https://api.iconify.design/lucide/clock.svg?color=%238b5cf6',
      permission: 'oAuth.accessToken.get',
      category: WIDGET_CATEGORY.TABLES,
      span: {
        minCols: 4,
        maxCols: 12,
        minRows: 4,
        maxRows: 8,
        defaultCols: 8,
        defaultRows: 5,
      },
      columns: accessTokenColumns,
      maxRows: 10,
      link: { route: '/o-auth/access-token' },
      source: {
        kind: 'graphql',
        document: accessTokenRecentQuery,
        variables: {
          query: { offset: 0, limit: 10, order: [{ field: 'createdAt', dir: 'desc' }] },
        },
        map: mapAccessTokensToRows,
        fetchPolicy: 'network-only',
      },
    }),
  ),
];
